'use client';

import {
  ChangeEvent,
  useContext,
  useEffect,
  useMemo,
  useState,
  type FC,
} from 'react';
import type { ColumnEditorProps } from './ColumnEditor.types';
import {
  Flex,
  Separator,
  ButtonGroup,
  Button,
  Select,
  Toggle,
} from '@/_components';
import styles from './ColumnEditor.module.css';
import { OverviewContext } from '@/_contexts/admin/OverviewProvider';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { OverviewColumnType } from '@/_types/overview';
import { DEFAULT_OVERVIEW_COLUMNS } from '@/_constants/overview';
import { reorderItems } from '@/_utils/draggable';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { QuestionnaireContext } from '@/_contexts/admin/QuestionnaireProvider';
import { updateOverviewSetting } from '@/_api/admin-users';
import { getCookie, setCookie } from 'cookies-next/client';
import { OVERVIEW_FIRM_ROLE_ID_COOKIE } from '@/_constants/cookies';
import { SortableColumnItem } from '../SortableColumnItem';

export const ColumnEditor: FC<ColumnEditorProps> = () => {
  const { columns, setColumns, resetColumns } = useContext(OverviewContext);
  const { addToast } = useContext(ToastContext);
  const { firmRoleOptions, preferences } = useContext(FirmSettingsContext);
  const { setUser, token, user, isFull, isPartner } = useContext(UserContext);
  const { loadQuestionnaires } = useContext(QuestionnaireContext);

  const [isLoading, setIsLoading] = useState(false);
  const [selectedFirmRole, setSelectedFirmRole] = useState<string>('');

  const selectedFirmRoleCookie = getCookie(OVERVIEW_FIRM_ROLE_ID_COOKIE);

  useEffect(() => {
    if (typeof selectedFirmRoleCookie === 'string') {
      setSelectedFirmRole(selectedFirmRoleCookie);
    }
  }, [selectedFirmRoleCookie]);

  const items = useMemo(
    () =>
      columns
        ?.map((c) => ({ id: c, content: c }))
        ?.filter((item) => item.id !== 'select' && item.id !== 'actions'),
    [columns],
  );

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over?.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const ordered: Array<OverviewColumnType> = reorderItems(
          items,
          oldIndex,
          newIndex,
        ).flatMap((item) => item.content);

        setColumns(ordered);
        addToast('success', 'Column order was updated');
      }
    }
  };

  const hasCustomColumns: boolean = columns !== DEFAULT_OVERVIEW_COLUMNS;

  const showNextReminderDate: boolean = columns.includes('next_reminder_date');
  const showFirmGroups: boolean = columns.includes('firm_group');
  const showEntityType: boolean = columns.includes('entity_type');
  const showSubmitDate: boolean = columns.includes('submitted_at');
  const showFirstUploadDate: boolean = columns.includes(
    'first_doc_uploaded_at',
  );
  const showFirmCustomStatuses: boolean = columns.includes(
    'firm_custom_status_id',
  );
  const showFirmRoles: boolean = columns.includes('firm_role');
  const showExternalEntityId: boolean = columns.includes('external_entity_id');
  const showExternalUserId: boolean = columns.includes('external_user_id');

  const onToggleColumn = (columnName: OverviewColumnType): void => {
    const hasColumn: boolean = columns.includes(columnName);
    const newColumns: Array<OverviewColumnType> = hasColumn
      ? columns.filter((c) => c !== columnName)
      : [...columns, columnName];

    setColumns(newColumns);
  };

  const onToggleLastReminder = () => onToggleColumn('next_reminder_date');
  const onToggleEntityType = () => onToggleColumn('entity_type');
  const onToggleSubmitDate = () => onToggleColumn('submitted_at');
  const onToggleFirstUploadDate = () => onToggleColumn('first_doc_uploaded_at');
  const onToggleFirmGroups = () => onToggleColumn('firm_group');
  const onToggleExternalEntityId = () => onToggleColumn('external_entity_id');
  const onToggleExternalUserId = () => onToggleColumn('external_user_id');
  const onToggleFirmCustomStatuses = () =>
    onToggleColumn('firm_custom_status_id');
  const onToggleFirmRoles = () => {
    onToggleColumn('firm_role');

    if (showFirmRoles) {
      setFirmRoleId('');
    }
  };

  const lastActivityShouldIncludeAllActivity: boolean =
    !!user?.overview_setting?.last_activity_should_include_all_activity;

  const onLastActivityChange = async ({
    target: { value },
  }: React.ChangeEvent<HTMLSelectElement>) => {
    if (!user) return;

    try {
      setIsLoading(true);
      await updateOverviewSetting({
        lastActivityShouldIncludeAllActivity: value === 'true',
        token,
      });
      user.overview_setting.last_activity_should_include_all_activity =
        value === 'true';
      setUser(user);
      loadQuestionnaires();
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setIsLoading(false);
    }
  };

  const onFirmRoleOptionSelect = (item: ChangeEvent<HTMLSelectElement>) =>
    setFirmRoleId(item.target.value);

  const setFirmRoleId = (id: string) => {
    if (typeof id !== 'string') return;

    setSelectedFirmRole(id);
    setCookie(OVERVIEW_FIRM_ROLE_ID_COOKIE, id);
    loadQuestionnaires();
  };

  return (
    <Flex isColumn={true} gap={8} className={styles.columnEditor}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={onDragEnd}
      >
        <SortableContext
          items={items.map((item) => item.id)}
          strategy={verticalListSortingStrategy}
        >
          <Flex gap={2} isColumn={true} className={styles.droppableContainer}>
            {items.map((item) => (
              <SortableColumnItem key={item.id} item={item} />
            ))}
          </Flex>
        </SortableContext>
      </DndContext>

      <Flex isColumn={true} gap={8}>
        <Flex isColumn={true} gap={4}>
          <Toggle
            label="Show Next Reminder Date"
            isSmall={true}
            onChange={onToggleLastReminder}
            isChecked={showNextReminderDate}
          />
          <Toggle
            label="Show Entity Type"
            isSmall={true}
            onChange={onToggleEntityType}
            isChecked={showEntityType}
          />
          <Toggle
            label="Show Submit Date"
            isSmall={true}
            onChange={onToggleSubmitDate}
            isChecked={showSubmitDate}
          />
          <Toggle
            label="Show First Document Upload Date"
            isSmall={true}
            onChange={onToggleFirstUploadDate}
            isChecked={showFirstUploadDate}
          />
          {preferences?.has_firm_groups && (isFull || isPartner) && (
            <Toggle
              label="Show Firm Group"
              isSmall={true}
              onChange={onToggleFirmGroups}
              isChecked={showFirmGroups}
            />
          )}
          {preferences?.custom_statuses_enabled && (
            <Toggle
              label="Custom Firm Statuses"
              isSmall={true}
              onChange={onToggleFirmCustomStatuses}
              isChecked={showFirmCustomStatuses}
            />
          )}
          {preferences?.use_firm_roles && (
            <Toggle
              label="Show Firm Role"
              isSmall={true}
              onChange={onToggleFirmRoles}
              isChecked={showFirmRoles}
            />
          )}
          {preferences?.external_entity_id_allowed && (
            <Toggle
              label="Show External Entity ID"
              isSmall={true}
              onChange={onToggleExternalEntityId}
              isChecked={showExternalEntityId}
            />
          )}
          {preferences?.external_entity_id_allowed && (
            <Toggle
              label="Show External User ID"
              isSmall={true}
              onChange={onToggleExternalUserId}
              isChecked={showExternalUserId}
            />
          )}
        </Flex>
        <Select
          helperText="Last Activity defaults to only activity by the client and the date that questionnaire was saved/sent. Change to All Activity to include system and firm user activity as well."
          isDisabled={isLoading}
          items={[
            { name: 'Only Client Activity', value: 'false' },
            {
              name: 'All Activity (System, Firm User, or Client)',
              value: 'true',
            },
          ]}
          label="Last Activity Represents"
          onChange={onLastActivityChange}
          selectedValue={
            lastActivityShouldIncludeAllActivity ? 'true' : 'false'
          }
        />
      </Flex>
      {preferences?.use_firm_roles && showFirmRoles && (
        <>
          <Separator isLight={true} />
          <Select
            label="Firm Role"
            placeholder="Select firm role"
            items={firmRoleOptions}
            selectedValue={selectedFirmRole}
            onChange={onFirmRoleOptionSelect}
          />
        </>
      )}
      {hasCustomColumns && (
        <>
          <Separator isLight={true} />
          <ButtonGroup align="right">
            <Button
              onClick={resetColumns}
              value="Reset Columns"
              intent="ghost"
              size="tiny"
            />
          </ButtonGroup>
        </>
      )}
    </Flex>
  );
};
